import {
  fetchEvaluationCategories,
  createEvaluation,
  fetchEvaluationList,
} from "@/service/evaluation.service";
import type { EvalRecordType } from "@/types/evaluation.model";
import { buildEvalRecordParams } from "@/types/evaluation.model";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import * as _ from "lodash";
import moment from "moment";
import { ROLES } from "@/utils/constants";
import { errorToast, evalCommonStyles } from "@/utils/constants";
import { selectUserRole } from "@/store/slices/authSlice";

const evaluationColumns: any[] = [
  { name: "", field: "name", style: { width: "35%" } },
  {
    name: "Weightage",
    field: "weightage",
    style: { width: "15%", textAlign: "center", marginRight: "20px" },
    fieldType: "input",
    type: "text",
    unit: "%",
    disabled: true,
  },
  {
    name: "Max Score",
    field: "max_score",
    style: { width: "15%", textAlign: "center", marginRight: "20px" },
    fieldType: "input",
    type: "text",
    disabled: true,
  },
  {
    name: "Enter Score",
    field: "score",
    style: { width: "35%", textAlign: "center" },
    fieldType: "input",
    type: "text",
    min: 0,
    maxField: "max_score",
  },
];
interface EvaluationProps {
  selectedStudentId: string;
  courseData?: { id: string };
  editable?: boolean;
  recordType?: EvalRecordType;
}

function Evaluation({
  selectedStudentId,
  courseData,
  editable,
  recordType = "course",
}: EvaluationProps) {
  const [evaluationList, setEvaluationList] = useState<any[]>([]);
  const userRole = useSelector(selectUserRole);
  const isAdmin = userRole?.code === ROLES.SUPER_ADMIN;
  const isStudent = userRole?.code === ROLES.STUDENT;
  const resourceId = courseData?.id;

  useEffect(() => {
    setEvaluationList([]);
    if (selectedStudentId && resourceId) {
      getEvaluationList();
    }
  }, [selectedStudentId, resourceId, recordType]);

  const getEvaluationList = () => {
    if (!resourceId) return;

    fetchEvaluationList({
      ...buildEvalRecordParams(resourceId, recordType),
      student_id: selectedStudentId,
    }).then((response) => {
      const data: any = _.cloneDeep(response.data);
      if (data?.count < 2 && resourceId && editable) {
        fetchEvaluationBase(data?.data);
      } else {
        formEvaluationList(data?.data);
      }
    });
  };

  const fetchEvaluationBase = async (evaluationList: any[]) => {
    if (!resourceId) return;

    fetchEvaluationCategories(buildEvalRecordParams(resourceId, recordType)).then(
      (response) => {
        const data: any[] = _.cloneDeep(response.data.data);
        const evalList: any[] = [
          ...evaluationList,
          ...[{ category_scores: data?.filter((cat) => cat?.name) }],
        ];
        formEvaluationList(evalList);
      },
    );
  };

  const formEvaluationList = (evalList: any[]) => {
    evalList?.forEach((evaluation: any) => {
      calculateOverall(evaluation);
    });
    setEvaluationList(evalList);
  };

  const calculateOverall = (evaluation: any, isUpdate?: boolean) => {
    const totalScore: number = isUpdate
      ? _.sumBy(
          evaluation?.category_scores,
          (item: any) => Number(item.score) || 0,
        )
      : evaluation?.overall_score || 0;
    const totalMaxScore: number = _.sumBy(
      evaluation?.category_scores,
      (item: any) => Number(item.max_score) || 0,
    );
    evaluation.overall_score = totalScore;
    evaluation.total_max_score = totalMaxScore;
    const percentage = isUpdate
      ? totalMaxScore
        ? ((totalScore / totalMaxScore) * 100).toFixed(2)
        : 0
      : evaluation?.overall_percentage || 0;
    evaluation.overall_percentage = percentage;
    setEvaluationList([...evaluationList]);
  };

  const numberRegex = /^\d+$/;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    column: any,
    category: any,
    evaluation: any,
  ) => {
    let value = e.target.value;
    // Remove unit if present and trim
    if (column.unit) {
      value = value.replace(column.unit, '').trim();
    }
    
    // Handle empty input or just "0"
    if (value === "" || value === "0") {
      category[column.field] = "0";
      calculateOverall(evaluation, true);
      return;
    }
    
    // Remove leading zeros but allow single "0"
    if (value.length > 1 && value.startsWith("0")) {
      value = value.replace(/^0+/, '');
    }
    
    if (
      !numberRegex.test(value) ||
      Number(value) < column.min ||
      Number(value) > category[column.maxField]
    ) {
      e.preventDefault();
      return;
    }

    category[column.field] = value;
    calculateOverall(evaluation, true);
  };

  const saveEvaluation = (evaluation: any) => {
    const category_scores: any[] = evaluation?.category_scores?.map(
      (cat: any) => {
        const scoreValue = cat.score?.trim();
        return { 
          category_id: cat.id, 
          score: !scoreValue || scoreValue === "" ? 0 : Number(scoreValue) 
        };
      },
    );
    const validScores = _.every(
      category_scores,
      (item) => _.isNumber(item.score) && item.score >= 0,
    );
    if (!validScores) {
      errorToast("Error", "Please enter valid scores");
      return;
    }
    if (!evaluation?.remarks) {
      errorToast("Error", "Please enter remarks");
      return;
    }
    if (!resourceId) {
      errorToast("Error", "Resource not found");
      return;
    }
    const payload = {
      ...buildEvalRecordParams(resourceId, recordType),
      student_id: selectedStudentId,
      category_scores: category_scores,
      overall_score: evaluation?.overall_score,
      overall_percentage: evaluation?.overall_percentage,
      remarks: evaluation?.remarks,
    };
    createEvaluation(payload).then((response) => {
      getEvaluationList();
    });
  };

  const formatedDate = (dateString: string) => {
    return moment(dateString, "DD/MM/YYYY HH:mm:ss").format("Do MMM, hh:mm A");
  };

  const handleRemarksChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>,
    evaluation: any,
  ) => {
    const value = e.target.value;
    // Limit to 200 characters
    if (value.length <= 200) {
      evaluation.remarks = value;
      setEvaluationList([...evaluationList]);
    } else {
      // Show error message for exceeding character limit
      errorToast("Error", "Remarks cannot exceed 200 characters");
    }
  };

  return (
    <div
      style={{
        ...evaluationStyles.mainContainer,
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <h1
        className="w-full text-left mb-0 py-[15px] px-[24px] border-b-[1px] border-[#E5E5E5]"
        style={{
          fontSize: "20px",
          fontWeight: "600",
          lineHeight: "100%",
          color: "#1F1F1F",
        }}
      >
        Evaluation
      </h1>
      <div className="py-[24px] px-[44px] flex w-full justify-evenly max-[1200px]:flex-col gap-[26px]">
        {evaluationList?.length ? (
          evaluationList?.map((evaluation: any, index: number) => (
            <div
              className="max-[1200px]:!w-full max-[1200px]:!ml-0"
              style={{
                ...evaluationStyles.container,
                ...{
                  marginLeft: index !== 0 ? "1vh" : undefined,
                  width: evaluationList?.length === 1 ? "75%" : "50%",
                },
              }}
            >
              <div style={evaluationStyles.containerHeader}>
                <h3 className="text-[#2E2E32]">Evaluation {index + 1}</h3>
              </div>
              <div style={evaluationStyles.containerContent}>
                <div style={evaluationStyles.tableHeaderRows}>
                  {evaluationColumns.map((column) => (
                    <div
                      className="text-[#2E2E32] text-[12px] font-[400]"
                      style={column.style}
                      key={column.field}
                    >
                      {column.name}
                    </div>
                  ))}
                </div>
                {evaluation?.category_scores?.map((category: any) => (
                  <div style={evaluationStyles.tableFieldRows}>
                    {evaluationColumns.map((column) =>
                      column.fieldType === "input" ? (
                          <input
                            type={column.type}
                            value={`${category[column.field] || "0"}${column.unit ? column.unit : ""}`}
                            style={{
                                ...column.style,
                              ...evaluationStyles.tableInput,
                              ...(column.disabled
                                ? evaluationStyles.tableInputDisabled
                                : {}),
                            }}
                            key={column.field}
                            disabled={column.disabled || evaluation?.id}
                            onChange={(event) =>
                              handleChange(event, column, category, evaluation)
                            }
                            onFocus={(e) => {
                              // Clear the default "0" when user focuses on the input
                              if (e.target.value === "0") {
                                e.target.select();
                              }
                            }}
                            onKeyDown={(e) => {
                              // Prevent space key
                              if (e.key === ' ') {
                                e.preventDefault();
                              }
                            }}
                            onPaste={(e) => {
                              // Prevent pasting spaces
                              e.preventDefault();
                              const pastedData = e.clipboardData.getData('text');
                              const cleanData = pastedData.replace(/\s/g, '');
                              document.execCommand('insertText', false, cleanData);
                            }}
                          />
                       
                      ) : (
                        <div
                          className="text-[#212529] mr-4"
                          style={column.style}
                          key={column.field}
                        >
                          {category[column.field]}
                        </div>
                      ),
                    )}
                  </div>
                ))}
                <div style={evaluationStyles.tableOverall}>
                  <div style={evaluationStyles.tableFieldRows}>
                    <div style={{ width: "35%", fontWeight: "bold" }}>
                      Overall Score
                    </div>
                    <div style={{ width: "30%", marginRight: "40px" }}></div>
                    <input
                      type="text"
                      value={`${evaluation?.overall_score} / ${evaluation?.total_max_score}`}
                      style={{
                        ...{ width: "35%" },
                        ...evaluationStyles.tableInput,
                      }}
                      disabled={true}
                    />
                  </div>
                  <div style={evaluationStyles.tableFieldRows}>
                    <div style={{ width: "35%", fontWeight: "bold" }}>
                      Overall Percentage
                    </div>
                    <div style={{ width: "30%", marginRight: "40px" }}></div>
                    <input
                      type="text"
                      value={evaluation?.overall_percentage + " %"}
                      style={{
                        ...{ width: "35%" },
                        ...evaluationStyles.tableInput,
                      }}
                      disabled={true}
                    />
                  </div>
                </div>
                <div style={evaluationStyles.tableRemarks}>
                  <div>
                    Remarks{!isAdmin && !isStudent && <span style={{ color: "red" }}>*</span>}
                  </div>
                  <textarea
                    style={{
                      width: "100%",
                      height: "80px",
                      marginTop: "5px",
                      borderRadius: "15px",
                      border: "1px solid #DADDE2",
                      padding: "8px",
                      resize: "none",
                      ...(evaluation?.id && {
                        backgroundColor: "#F6F7FA",
                        color: "#6B7280",
                        cursor: "not-allowed",
                      }),
                    }}
                    placeholder="Enter a note"
                    value={evaluation?.remarks}
                    onChange={(e) => handleRemarksChange(e, evaluation)}
                    disabled={evaluation?.id || isAdmin || isStudent}
                  ></textarea>
                </div>
                <div
                  style={{
                    ...evaluationStyles.tableFooter,
                    ...{
                      justifyContent: evaluation?.id
                        ? "space-between"
                        : "flex-end",
                    },
                  }}
                >
                  {evaluation?.id ? (
                    <>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <img src="/account_circle.svg" alt="account" />
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            marginLeft: "8px",
                          }}
                        >
                          <div style={evaluationStyles.createdName}>
                            {evaluation?.created_by}
                          </div>
                          <div style={evaluationStyles.createdDate}>
                            {formatedDate(evaluation?.created_on)}
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          ...evalCommonStyles.feedback,
                          ...evalCommonStyles[
                            evaluation?.feedback
                              ?.toLowerCase()
                              .replace(/\s+/g, "_")
                          ],
                        }}
                      >
                        {evaluation?.feedback}
                      </div>
                    </>
                  ) : (
                    <button
                      style={evaluationStyles.saveButton}
                      onClick={() => saveEvaluation(evaluation)}
                    >
                      Save
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignContent: "center",
              height: "100%",
              width: "100%",
            }}
          >
            No Evaluations Found
          </div>
        )}
      </div>
    </div>
  );
}
export default Evaluation;

export const evaluationStyles: any = {
  mainContainer: {
    borderRadius: "12px",
    borderWidth: "1px",
    width: "100%",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EEEEEE",
    display: "flex",
    justifyContent: "space-evenly",
    minHeight: "60vh",
    height: "auto",
  },
  container: {
    boxShadow: "-3px -3px 104px 12px #1F1F1F08",
    border: "1px solid #D9D9D9",
    height: "100%",
    backgroundColor: "#FFFFFF",
    borderRadius: "18px",
  },
  containerHeader: {
    height: "10%",
    background: "#F8F8F8",
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    borderRadius: "18px 18px 0 0",
  },
  containerContent: {
    height: "90%",
  },
  tableHeaderRows: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 16px 0 16px",
    width: "100%",
  },
  tableFieldRows: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 16px",
    width: "100%",
    alignItems: "center",
  },
  tableInput: {
    borderRadius: "6px",
    padding: "8px",
    textAlign: "center",
    border: "1px solid #DADDE2",
    background: "#FFFFFF",
  },
  tableInputDisabled: {
    background: "#F6F7FA",
  },
  tableOverall: {
    background: "#F6FAFF",
    marginTop: "10px",
    paddingTop: "10px",
    paddingBottom: "10px",
  },
  tableRemarks: {
    marginTop: "10px",
    padding: "8px 16px",
  },
  tableFooter: {
    marginTop: "10px",
    borderTop: "1px solid #E5E7EB",
    padding: "12px 16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  createdName: {
    fontWeight: 600,
    fontSize: "16px",
    color: "#1F1F1F",
  },
  createdDate: {
    fontWeight: 600,
    fontSize: "14px",
    color: "#CAC7C7",
  },
  saveButton: {
    background: "#5C88C5",
    color: "#FFFFFF",
    padding: "8px 12px",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    float: "right",
    minWidth: "162px",
    minHeight: "40px",
  },
  saveButtonDisabled: {
    background: "#84A5D3",
    cursor: "not-allowed",
  },
};
